<template>
  <!-- Settings-menu row (studio-design `tv-nav-item`): icon + label; default
       surface-700 + surface-500 icon, hover surface-100, active primary-500 fill
       + white. Collapses to an icon-only rail when the nav is minimized. -->
  <li class="w-full">
    <div
      v-if="loading"
      class="h-9 rounded-lg animate-pulse bg-surface-200"
      :class="collapsed ? 'w-9 mx-auto' : 'w-full'"
    ></div>

    <NuxtLink
      v-else-if="to"
      v-tooltip.right="collapsed ? label : undefined"
      :to="to"
      class="group flex items-center gap-2.5 rounded-lg py-2 transition-colors"
      :class="[
        collapsed ? 'justify-center px-0' : 'px-2.5',
        isActive
          ? 'bg-primary-500 text-white shadow-[0_1px_2px_0_rgba(79,70,229,0.25)]'
          : 'text-surface-700 hover:bg-surface-100 hover:text-surface-900',
      ]"
      :data-testid="testid"
    >
      <span
        class="flex w-7 shrink-0 items-center justify-center text-[18px] leading-none"
        :class="
          isActive
            ? 'text-white'
            : 'text-surface-500 group-hover:text-surface-700'
        "
      >
        <font-awesome-icon
          :icon="icon"
          fixed-width
        />
      </span>
      <template v-if="!collapsed">
        <span class="flex-1 truncate text-sm font-medium">{{ label }}</span>
        <slot name="after"></slot>
      </template>
    </NuxtLink>

    <div
      v-else
      class="flex items-center gap-2.5 rounded-lg py-2 text-surface-700"
      :class="collapsed ? 'justify-center px-0' : 'px-2.5'"
    >
      <span
        class="flex w-7 shrink-0 items-center justify-center text-[18px] leading-none text-surface-500"
      >
        <font-awesome-icon
          :icon="icon"
          fixed-width
        />
      </span>
      <template v-if="!collapsed">
        <span class="flex-1 truncate text-sm font-medium">{{ label }}</span>
        <slot name="after"></slot>
      </template>
    </div>
  </li>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

const route = useRoute()

const collapsed = inject<Ref<boolean>>('navCollapsed', ref(false))

const props = defineProps<{
  label: string
  icon: string
  to?: RouteLocationRaw
  loading?: boolean
  testid?: string
}>()

function isNamedRouteActive(targetName: string): boolean {
  const current = route.name?.toString()
  if (!current) return false
  return current === targetName || current.startsWith(`${targetName}-`)
}

const isActive = computed(() => {
  if (typeof props.to === 'string') {
    const path = route.path
    return path === props.to || path.startsWith(`${props.to}/`)
  }

  if (
    props.to &&
    typeof props.to === 'object' &&
    'name' in props.to &&
    props.to.name
  ) {
    return isNamedRouteActive(props.to.name.toString())
  }

  return false
})
</script>
